import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Image
} from '@react-pdf/renderer';
import { DashboardFaturamentoDetalheItem } from '../../../types/dashboard';
import { getEtapaLabel } from '../../crm/empenhos/utils/etapaUtils';
import { EtapaEmpenho } from '../../../types/empenhos';

// Estilos do documento - Paisagem (mesmo padrão de RelatorioFinanceiroDocument.tsx)
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 20,
        fontFamily: 'Helvetica',
        fontSize: 7,
        color: '#333333',
    },
    headerBox: {
        flexDirection: 'row',
        marginBottom: 10,
        borderBottom: '0.5px solid #e0e0e0',
        paddingBottom: 8,
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    title: {
        fontSize: 14,
        fontFamily: 'Helvetica-Bold',
        color: '#333333',
    },
    subtitle: {
        fontSize: 9,
        color: '#666666',
        marginTop: 2,
    },
    logo: {
        width: 100,
        height: 40,
        objectFit: 'contain',
    },
    tableContainer: {
        marginTop: 5,
        borderWidth: 0.5,
        borderColor: '#e0e0e0',
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f5f5f5',
        borderBottomWidth: 0.5,
        borderBottomColor: '#e0e0e0',
        alignItems: 'center',
        height: 15,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#e0e0e0',
        alignItems: 'center',
        minHeight: 12,
        paddingVertical: 2,
    },
    // Colunas (Total 100%)
    colId: { width: '5%', paddingLeft: 4 },
    colCodigo: { width: '11%', paddingLeft: 4 },
    colDataEmpenho: { width: '9%', paddingLeft: 4 },
    colContrato: { width: '28%', paddingLeft: 4 },
    colValor: { width: '11%', textAlign: 'right', paddingRight: 4 },
    colEtapa: { width: '10%', paddingLeft: 4 },
    colCondPgto: { width: '8%', paddingLeft: 4 },
    colNfVenda: { width: '9%', paddingLeft: 4 },
    colDataNf: { width: '9%', paddingLeft: 4 },

    headerText: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 6,
    },
    cellText: {
        fontSize: 6,
    },
    totalRow: {
        flexDirection: 'row',
        alignItems: 'center',
        minHeight: 16,
        paddingVertical: 3,
        backgroundColor: '#eef1f6',
        borderTopWidth: 0.8,
        borderTopColor: '#2f3d52',
    },
    totalText: {
        fontSize: 7,
        fontFamily: 'Helvetica-Bold',
    },
    footer: {
        position: 'absolute',
        bottom: 10,
        left: 20,
        right: 20,
        textAlign: 'center',
        fontSize: 6,
        color: '#999999',
        borderTopWidth: 0.5,
        borderTopColor: '#e0e0e0',
        paddingTop: 4,
    },
});

interface RelatorioAutorizacoesFaturamentoDocumentProps {
    itens: DashboardFaturamentoDetalheItem[];
    resumo: {
        valor_total: number;
        total_notas: number;
    };
    periodoLabel: string;
    logoUrl?: string;
}

const formatCurrency = (value: number) => {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const parts = dateString.split('-'); // YYYY-MM-DD
    if (parts.length !== 3) return dateString;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const CONDICAO_PAGAMENTO_LABELS: Record<string, string> = {
    A_VISTA: 'À Vista',
    A_PRAZO: 'A Prazo',
};

export const RelatorioAutorizacoesFaturamentoDocument: React.FC<RelatorioAutorizacoesFaturamentoDocumentProps> = ({
    itens,
    resumo,
    periodoLabel,
    logoUrl = '/assets/img/A2R_logo.png'
}) => {
    return (
        <Document>
            <Page size="A4" orientation="landscape" style={styles.page}>

                {/* Cabeçalho */}
                <View style={styles.headerBox}>
                    <View>
                        <Text style={styles.title}>Relatório de Autorizações — Faturamento</Text>
                        <Text style={styles.subtitle}>Período: {periodoLabel}</Text>
                        <Text style={styles.subtitle}>Gerado em: {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}</Text>
                    </View>
                    <Image src={logoUrl} style={styles.logo} />
                </View>

                {/* Tabela */}
                <View style={styles.tableContainer}>
                    {/* Cabeçalho da Tabela */}
                    <View style={styles.tableHeader}>
                        <View style={styles.colId}><Text style={styles.headerText}>ID</Text></View>
                        <View style={styles.colCodigo}><Text style={styles.headerText}>CÓDIGO</Text></View>
                        <View style={styles.colDataEmpenho}><Text style={styles.headerText}>DATA EMPENHO</Text></View>
                        <View style={styles.colContrato}><Text style={styles.headerText}>CONTRATO</Text></View>
                        <View style={styles.colValor}><Text style={styles.headerText}>VALOR TOTAL</Text></View>
                        <View style={styles.colEtapa}><Text style={styles.headerText}>ETAPA</Text></View>
                        <View style={styles.colCondPgto}><Text style={styles.headerText}>COND. PGTO</Text></View>
                        <View style={styles.colNfVenda}><Text style={styles.headerText}>NF VENDA</Text></View>
                        <View style={styles.colDataNf}><Text style={styles.headerText}>DATA EMISSÃO NF</Text></View>
                    </View>

                    {/* Linhas */}
                    {itens.map((item, index) => (
                        <View key={item.id} style={[
                            styles.tableRow,
                            { backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9' }
                        ]}>
                            <View style={styles.colId}>
                                <Text style={styles.cellText}>{item.id}</Text>
                            </View>
                            <View style={styles.colCodigo}>
                                <Text style={styles.cellText}>{item.codigo_empenho || '-'}</Text>
                            </View>
                            <View style={styles.colDataEmpenho}>
                                <Text style={styles.cellText}>{formatDate(item.data_empenho)}</Text>
                            </View>
                            <View style={styles.colContrato}>
                                <Text style={styles.cellText}>
                                    {item.contrato_descricao ? `#${item.id_contrato} - ${item.contrato_descricao}` : `Contrato #${item.id_contrato}`}
                                </Text>
                            </View>
                            <View style={styles.colValor}>
                                <Text style={styles.cellText}>{formatCurrency(item.valor_total)}</Text>
                            </View>
                            <View style={styles.colEtapa}>
                                <Text style={styles.cellText}>{getEtapaLabel(item.etapa as EtapaEmpenho)}</Text>
                            </View>
                            <View style={styles.colCondPgto}>
                                <Text style={styles.cellText}>
                                    {item.condicao_pagamento ? (CONDICAO_PAGAMENTO_LABELS[item.condicao_pagamento] || item.condicao_pagamento) : '-'}
                                </Text>
                            </View>
                            <View style={styles.colNfVenda}>
                                <Text style={styles.cellText}>{item.numero_nf_venda || '-'}</Text>
                            </View>
                            <View style={styles.colDataNf}>
                                <Text style={styles.cellText}>{formatDate(item.data_emissao_nf_venda)}</Text>
                            </View>
                        </View>
                    ))}

                    {/* Linha de total */}
                    <View style={styles.totalRow}>
                        <View style={{ width: '53%', paddingLeft: 4 }}>
                            <Text style={styles.totalText}>TOTAL ({resumo.total_notas} {resumo.total_notas === 1 ? 'nota' : 'notas'})</Text>
                        </View>
                        <View style={styles.colValor}>
                            <Text style={styles.totalText}>{formatCurrency(resumo.valor_total)}</Text>
                        </View>
                        <View style={{ width: '36%' }} />
                    </View>
                </View>

                {/* Rodapé da Página */}
                <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
                    `Página ${pageNumber} de ${totalPages}`
                )} fixed />
            </Page>
        </Document>
    );
};
